import { motion } from "framer-motion";
import { RefreshCw, Clock, FileCheck, Download, Printer, Users } from "lucide-react";
import { COMMITMENT_ICON_SRCS } from "../components/icons/CommitmentIcons";
import { COMMITMENTS, IMAGES } from "../lib/content";
import { useCommentary } from "../context/CommentaryContext";
import { PageHero } from "../components/PageHero";
import { CtaBand } from "./Home";

const cardVariant = {
  hidden: { opacity: 0, y: 55, scale: 0.96 },
  show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
};

export default function Commitments() {
  const { stop } = useCommentary();
  return (
    <div data-testid="commitments-page">
      <PageHero
        eyebrow="The Board's Commitment · v0.1"
        title="Our Board's Commitment to Tough Trust."
        intro="Ten points, each earned the hard way - by something that went right, or more often by something that went wrong on a Board where soft trust resulted in people quietly looking the other way."
        image={IMAGES.heroBg}
      />

      {/* INTRO VIDEO */}
      <section className="bg-ivory py-16 md:py-20" data-testid="method-intro-video-section">
        <div className="container-px max-w-4xl mx-auto">
          <video
            data-testid="method-intro-video"
            controls
            playsInline
            preload="none"
            poster="/video-method.jpg"
            onPlay={stop}
            className="w-full aspect-video bg-black border border-ink/10 shadow-xl shadow-ink/10"
          >
            <source src="/video-method.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          <h2 className="font-serif text-2xl md:text-3xl text-ink leading-snug mt-5" data-testid="method-intro-video-title">
            The Tough Trust Method – an introduction
          </h2>
        </div>
      </section>

      {/* CARDS – high-contrast dark stage */}
      <section className="relative py-20 md:py-28 bg-gradient-to-b from-ink via-navy to-ink overflow-hidden">
        <div className="noise-overlay" />
        <div
          className="absolute top-1/3 -left-40 w-[500px] h-[500px] rounded-full bg-windsor/20 blur-[120px]"
          aria-hidden
        />
        <div className="container-px max-w-4xl relative z-10">
          <p className="text-ivory/50 text-sm text-center mb-8" data-testid="commitments-hint">
            Ten Board-level commitments – each one earned the hard way.
          </p>
          <div className="space-y-7">
            {COMMITMENTS.map((c, i) => {
              const ghostSrc = COMMITMENT_ICON_SRCS[i % COMMITMENT_ICON_SRCS.length];
              return (
              <motion.article
                key={c.n}
                data-testid={`commitment-${c.n}`}
                variants={cardVariant}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-70px" }}
                className="group relative bg-white p-8 md:p-11 overflow-hidden shadow-xl shadow-black/20 transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl hover:shadow-black/40"
              >
                {/* ghost icon – faint shadow by default, a touch bolder on hover */}
                <img
                  src={ghostSrc}
                  alt=""
                  aria-hidden
                  className="pointer-events-none select-none absolute right-5 top-5 w-32 h-32 md:w-40 md:h-40 object-contain opacity-[0.13] transition-opacity duration-500 group-hover:opacity-[0.32]"
                />
                {/* accent bar – spreads on hover */}
                <span className="absolute top-0 left-0 h-full bg-windsor w-1.5 transition-all duration-500 group-hover:w-3" />
                <div className="relative flex items-baseline gap-5 pl-3">
                  <span className="font-serif text-5xl md:text-6xl text-windsor shrink-0 transition-transform duration-500 group-hover:scale-105">
                    {c.n}
                  </span>
                  <h2 className="font-serif text-3xl md:text-4xl lg:text-[2.6rem] leading-[1.08] tracking-tight text-ink transition-colors duration-300 group-hover:text-windsor">
                    {c.title}
                  </h2>
                </div>
                <p className="relative text-ink text-lg md:text-xl font-light leading-relaxed mt-6 pl-3">
                  {c.body}
                </p>
                <div className="ml-3 mt-7 bg-cloud border-l-4 border-gold pl-5 py-4">
                  <p className="overline">Why it's here</p>
                  <p className="text-ink/85 text-base md:text-lg leading-relaxed mt-2">
                    {c.why}
                  </p>
                </div>
              </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Evolves note */}
      <section className="py-24 md:py-28 bg-ivory" data-testid="evolves-section">
        <div className="container-px max-w-6xl">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            {/* Version badge */}
            <motion.div
              className="lg:col-span-4"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            >
              <p className="overline">How it evolves</p>
              <div className="mt-5 flex items-end gap-3">
                <span className="font-serif text-7xl md:text-8xl text-windsor leading-none tracking-tight">
                  v0.1
                </span>
                <span className="font-serif italic text-2xl text-ink/50 mb-2">and counting</span>
              </div>
              <p className="text-ink/80 font-light leading-relaxed mt-6">
                The Commitment carries a version number, and that is not a placeholder – it is a
                promise to keep rewriting it.
              </p>
            </motion.div>

            {/* Point cards */}
            <div className="lg:col-span-8 space-y-4">
              {[
                {
                  icon: FileCheck,
                  title: "An honest admission",
                  body: "V0.1 admits that no Board got this right on the first writing – and that pretending otherwise is the first failure of soft trust.",
                },
                {
                  icon: RefreshCw,
                  title: "Built to be rewritten",
                  body: "Every Board that adopts the Commitment refines it. Evolution is not weakness – it is the point.",
                },
                {
                  icon: Clock,
                  title: "The eighteen-month rule",
                  body: "A Commitment that has not been rewritten in eighteen months is a Commitment that has lost its edge.",
                },
              ].map((p, i) => (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                  className="group flex gap-5 bg-white border border-ink/10 p-6 md:p-7 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-400"
                >
                  <span className="shrink-0 w-12 h-12 grid place-items-center rounded-full bg-windsor/10 text-windsor group-hover:bg-windsor group-hover:text-ivory transition-colors duration-300">
                    <p.icon size={22} />
                  </span>
                  <div>
                    <h3 className="font-serif text-xl md:text-2xl text-ink leading-snug">{p.title}</h3>
                    <p className="text-ink/80 font-light leading-relaxed mt-2">{p.body}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Closing statement */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="relative overflow-hidden bg-ink text-ivory p-9 md:p-12 mt-8"
          >
            <span
              className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-windsor/25 blur-[90px]"
              aria-hidden
            />
            <p className="relative font-serif text-2xl md:text-3xl lg:text-4xl leading-snug tracking-tight max-w-4xl">
              Tough trust <em className="text-gold not-italic">is</em> difficult – the only way we make
              it real is by practising it and developing it every day of every week.
            </p>
          </motion.div>
        </div>
      </section>

      {/* DOWNLOAD ONE-PAGER */}
      <section className="bg-ivory py-20 md:py-28" data-testid="method-download-section">
        <div className="container-px max-w-3xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 w-14 h-14 justify-center rounded-full bg-windsor/10 text-windsor mx-auto">
            <Users size={24} />
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-ink leading-tight mt-6">
            Share it with your team
          </h2>
          <p className="text-ink/80 text-lg font-light leading-relaxed mt-5">
            Take the ten commitments into your next Board meeting. Download the one-page summary
            of the Tough Trust Method to circulate and discuss with your leadership team.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mt-9">
            <a
              href="/tough-trust-method-digital.pdf"
              download="Tough-Trust-Method.pdf"
              data-testid="method-download-digital"
              className="group relative w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#C2410C] text-ivory pl-8 pr-3 py-3.5 rounded-full font-semibold text-base md:text-lg shadow-xl shadow-ink/20 overflow-hidden transition-all duration-300 hover:scale-[1.04] hover:shadow-2xl"
            >
              <span
                aria-hidden
                className="absolute inset-0 bg-ink translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out"
              />
              <span className="relative z-10">Download to share</span>
              <span className="relative z-10 grid place-items-center w-11 h-11 rounded-full bg-ivory text-windsor transition-transform duration-300 group-hover:translate-y-0.5">
                <Download size={20} />
              </span>
            </a>
            <a
              href="/tough-trust-method-print.pdf"
              download="Tough-Trust-Method-Print.pdf"
              data-testid="method-download-print"
              className="group relative w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-white text-ink border-2 border-ink/15 pl-8 pr-3 py-3 rounded-full font-semibold text-base md:text-lg shadow-lg shadow-ink/10 overflow-hidden transition-all duration-300 hover:scale-[1.04] hover:border-windsor/40 hover:shadow-xl"
            >
              <span className="relative z-10">Download to print</span>
              <span className="relative z-10 grid place-items-center w-11 h-11 rounded-full bg-windsor text-ivory transition-transform duration-300 group-hover:translate-y-0.5">
                <Printer size={20} />
              </span>
            </a>
          </div>
          <p className="text-ink/55 text-sm mt-4">
            Colour PDF for digital circulation · white PDF optimised for printing
          </p>
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
